import React, { useState, useEffect } from 'react';
import api from '../../api';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search } from 'lucide-react';
import { STATUS_ORDER, STATUS_LABELS } from '../../components/StatusPill';

export const PLACEHOLDER_HINTS = ['{{first_name}}', '{{contact_name}}', '{{company_name}}', '{{title}}'];

export function CampaignDetailsAndAudience({ form, setForm, companies, search, setSearch, companyFilters, setCompanyFilters, showAudience = true }) {
    const [leadSources, setLeadSources] = useState([]);
    useEffect(() => {
        api.get('/clients/lead-sources').then(r => setLeadSources(r.data.items || [])).catch(() => {});
    }, []);
    const toggleCompany = (id) => {
        setForm(f => ({ ...f, client_ids: f.client_ids.includes(id) ? f.client_ids.filter(x => x !== id) : [...f.client_ids, id] }));
    };
    const visibleIds = companies.map(c => c.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => form.client_ids.includes(id));
    const toggleSelectAll = () => {
        setForm(f => ({
            ...f,
            client_ids: allVisibleSelected
                ? f.client_ids.filter(id => !visibleIds.includes(id))
                : [...new Set([...f.client_ids, ...visibleIds])],
        }));
    };
    return (
        <div className="space-y-5">
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                <div className="font-display text-[15px] font-semibold mb-4">Campaign details</div>
                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Campaign name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                       placeholder="UK H&S / CDM Decision Makers — Aug 2026" className="mt-1.5 h-10" data-testid="campaign-name-input" />

                <div className="mt-4">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Subject</Label>
                    <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                           placeholder="Quick question for {{first_name}} at {{company_name}}" className="mt-1.5 h-10" data-testid="campaign-subject-input" />
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] text-[color:var(--bm-text-subtle)]">Placeholders:</span>
                        {PLACEHOLDER_HINTS.map(p => (
                            <button key={p} type="button" onClick={() => setForm(f => ({ ...f, subject: f.subject + ' ' + p }))}
                                    className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[color:var(--bm-bg-mute)] border border-[color:var(--bm-border)] hover:bg-[color:var(--bm-accent-50)]"
                                    data-testid={`campaign-subject-placeholder-${p.replace(/[{}]/g, '')}`}>
                                {p}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4">
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Body</Label>
                    <Textarea value={form.body_html} onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))}
                              placeholder={`Hi {{first_name}},\n\nI noticed {{company_name}} ...`} className="mt-1.5 min-h-[220px] font-mono text-[13px]" data-testid="campaign-body-input" />
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] text-[color:var(--bm-text-subtle)]">Placeholders:</span>
                        {PLACEHOLDER_HINTS.map(p => (
                            <button key={p} type="button" onClick={() => setForm(f => ({ ...f, body_html: f.body_html + ' ' + p }))}
                                    className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[color:var(--bm-bg-mute)] border border-[color:var(--bm-border)] hover:bg-[color:var(--bm-accent-50)]">
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {showAudience && (
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
                <div className="font-display text-[15px] font-semibold mb-4">Companies to target</div>
                <div className="relative mb-3">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-subtle)]" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies…" className="h-9 pl-9" data-testid="campaign-company-search" />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                        <Label className="text-[11px] font-medium text-[color:var(--bm-text-muted)]">Status / Stage</Label>
                        <Select value={companyFilters.status} onValueChange={(v) => setCompanyFilters(f => ({ ...f, status: v }))}>
                            <SelectTrigger className="h-9 mt-1" data-testid="campaign-company-status-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__any__">Any status</SelectItem>
                                {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[11px] font-medium text-[color:var(--bm-text-muted)]">Contact email contains</Label>
                        <Input value={companyFilters.contact_email} onChange={e => setCompanyFilters(f => ({ ...f, contact_email: e.target.value }))}
                               placeholder="e.g. @nhs.uk" className="h-9 mt-1" data-testid="campaign-company-contact-email-filter" />
                    </div>
                    <div>
                        <Label className="text-[11px] font-medium text-[color:var(--bm-text-muted)]">Date added from</Label>
                        <Input type="date" value={companyFilters.date_from} onChange={e => setCompanyFilters(f => ({ ...f, date_from: e.target.value }))}
                               className="h-9 mt-1" data-testid="campaign-company-date-from-filter" />
                    </div>
                    <div>
                        <Label className="text-[11px] font-medium text-[color:var(--bm-text-muted)]">Date added to</Label>
                        <Input type="date" value={companyFilters.date_to} onChange={e => setCompanyFilters(f => ({ ...f, date_to: e.target.value }))}
                               className="h-9 mt-1" data-testid="campaign-company-date-to-filter" />
                    </div>
                </div>
                <div className="mb-3">
                    <Label className="text-[11px] font-medium text-[color:var(--bm-text-muted)]">Lead source</Label>
                    <Select value={companyFilters.source} onValueChange={(v) => setCompanyFilters(f => ({ ...f, source: v }))}>
                        <SelectTrigger className="h-9 mt-1" data-testid="campaign-company-source-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__any__">Any lead source</SelectItem>
                            {leadSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <p className="text-[10.5px] text-[color:var(--bm-text-subtle)] mt-1">Helps you see exactly which contacts you're about to email — e.g. only companies sourced from LinkedIn or a specific outreach campaign.</p>
                </div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">
                        {form.client_ids.length === 0 ? 'None selected = all companies with eligible contacts' : `${form.client_ids.length} company/companies selected`}
                        {companies.length > 0 && <span> · {companies.length} matching this filter</span>}
                    </div>
                    {companies.length > 0 && (
                        <button type="button" onClick={toggleSelectAll} className="text-[11.5px] font-medium text-[color:var(--bm-primary)] hover:underline shrink-0" data-testid="campaign-company-select-all-btn">
                            {allVisibleSelected ? 'Clear all shown' : `Select all ${companies.length} shown`}
                        </button>
                    )}
                </div>
                <div className="max-h-[260px] overflow-y-auto border border-[color:var(--bm-border)] rounded-lg divide-y divide-[color:var(--bm-border)]">
                    {companies.map(c => (
                        <label key={c.id} className="flex items-center gap-2.5 px-3 py-2 text-[13px] cursor-pointer hover:bg-[color:var(--bm-bg-mute)]" data-testid={`campaign-company-option-${c.id}`}>
                            <Checkbox checked={form.client_ids.includes(c.id)} onCheckedChange={() => toggleCompany(c.id)} />
                            <span className="flex-1">{c.company_name}</span>
                            <span className="text-[10px] uppercase tracking-wide text-[color:var(--bm-text-subtle)]">{STATUS_LABELS[c.status] || c.status}</span>
                        </label>
                    ))}
                    {companies.length === 0 && <div className="px-3 py-6 text-center text-[12.5px] text-[color:var(--bm-text-subtle)]">No companies found</div>}
                </div>
            </div>
            )}
        </div>
    );
}

export function CampaignCadenceSettings({ form, setForm, showAudienceOptions = true }) {
    const [bounceBatches, setBounceBatches] = useState([]);
    useEffect(() => {
        if (!showAudienceOptions) return;
        api.get('/leadgen/import/contacts-update/history').then(r => setBounceBatches(r.data.items || [])).catch(() => {});
    }, [showAudienceOptions]);
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
            <div className="font-display text-[15px] font-semibold mb-4">{showAudienceOptions ? 'Selection & cadence' : 'Cadence'}</div>
            {showAudienceOptions && (
            <>
            <label className="flex items-center gap-2.5 text-[13px] mb-3 cursor-pointer">
                <Checkbox checked={form.only_new_leads} onCheckedChange={(v) => setForm(f => ({ ...f, only_new_leads: !!v }))} data-testid="campaign-only-new-leads-checkbox" />
                Only new leads <span className="text-[color:var(--bm-text-subtle)]">(excludes paying/active clients)</span>
            </label>
            <div className="mb-3">
                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Industry / keyword</Label>
                <Input value={form.keyword} onChange={e => setForm(f => ({ ...f, keyword: e.target.value }))}
                       placeholder="e.g. construction, health and safety" className="mt-1.5 h-10" data-testid="campaign-keyword-input" />
                <p className="text-[10.5px] text-[color:var(--bm-text-subtle)] mt-1">Matches company description, fit, tech signal, industry or notes.</p>
            </div>
            <label className="flex items-center gap-2.5 text-[13px] mb-3 cursor-pointer">
                <Checkbox checked={form.verified_only} onCheckedChange={(v) => setForm(f => ({ ...f, verified_only: !!v }))} data-testid="campaign-verified-only-checkbox" />
                Verified emails only
            </label>
            <label className="flex items-center gap-2.5 text-[13px] mb-4 cursor-pointer">
                <Checkbox checked={form.exclude_contacted} onCheckedChange={(v) => setForm(f => ({ ...f, exclude_contacted: !!v }))} data-testid="campaign-exclude-contacted-checkbox" />
                Exclude contacts already contacted
            </label>
            <div className="mb-4">
                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Resend to a bounce-corrected batch</Label>
                <Select value={form.bounce_correction_import_id || '__none__'} onValueChange={(v) => setForm(f => ({ ...f, bounce_correction_import_id: v === '__none__' ? null : v }))}>
                    <SelectTrigger className="h-9 mt-1.5" data-testid="campaign-bounce-batch-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">None — use company filters only</SelectItem>
                        {bounceBatches.map(b => (
                            <SelectItem key={b.id} value={b.id}>
                                {b.filename} — {new Date(b.committed_at).toLocaleDateString('en-GB')} — {b.contacts_updated} corrected
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-[10.5px] text-[color:var(--bm-text-subtle)] mt-1">Narrows the audience to exactly the contacts fixed in that bounce-correction import, on top of any company filters above.</p>
            </div>
            </>
            )}
            {!showAudienceOptions && (
                <p className="text-[11.5px] text-[color:var(--bm-text-subtle)] mb-4">Audience is already locked in for this campaign (recipients were queued) — cadence and content can still be adjusted.</p>
            )}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Seconds between emails</Label>
                    <Input type="number" min={30} value={form.cadence_seconds} onChange={e => setForm(f => ({ ...f, cadence_seconds: Number(e.target.value) || 90 }))} className="mt-1.5 h-10" data-testid="campaign-cadence-input" />
                </div>
                <div>
                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Max per day</Label>
                    <Input type="number" min={1} value={form.daily_cap} onChange={e => setForm(f => ({ ...f, daily_cap: Number(e.target.value) || 100 }))} className="mt-1.5 h-10" data-testid="campaign-daily-cap-input" />
                </div>
            </div>
            <p className="text-[11px] text-[color:var(--bm-text-subtle)] mt-2">
                Do Not Contact and permanently suppressed contacts are always excluded — every email is sent individually, never BCC.
            </p>
        </div>
    );
}
